import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from "react-router-dom";

const formatJoinedDate = (value) => {
  if (!value) {
    return 'Not listed';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Not listed';
  }

  return date.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatPhoneNumber = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  return value || 'Not listed';
};

const hasListedValue = (value) => Boolean(String(value || '').trim());

const SHOP_OPENING_HOUR_DAYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const formatOpeningHourTime = (value) => {
  const timeValue = String(value || '').trim();

  if (!timeValue) {
    return '';
  }

  const timeMatch = timeValue.match(/^(\d{1,2}):(\d{2})/);
  if (!timeMatch) {
    return timeValue;
  }

  const date = new Date();
  date.setHours(Number(timeMatch[1]), Number(timeMatch[2]), 0, 0);

  return date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getShopOpeningHours = (openingHours) => {
  if (!openingHours) {
    return [];
  }

  if (Array.isArray(openingHours?.weekday_text)) {
    return openingHours.weekday_text
      .filter(Boolean)
      .map((label) => ({ label }));
  }

  if (Array.isArray(openingHours)) {
    return openingHours
      .map((entry) => {
        if (typeof entry === 'string') {
          return { label: entry };
        }

        return {
          day: entry?.day || '',
          open: entry?.openTime || entry?.open || '',
          close: entry?.closeTime || entry?.close || '',
        };
      })
      .filter((entry) => entry.label || entry.open || entry.close);
  }

  if (typeof openingHours === 'object') {
    return SHOP_OPENING_HOUR_DAYS.map(({ key, label }) => ({
      day: label,
      open: openingHours[key]?.openTime || openingHours[key]?.open || '',
      close: openingHours[key]?.closeTime || openingHours[key]?.close || '',
    })).filter((entry) => entry.open || entry.close);
  }

  return [];
};

const formatOpeningHoursRange = (entry) => {
  if (entry.label) {
    return entry.label;
  }

  const openTime = formatOpeningHourTime(entry.open);
  const closeTime = formatOpeningHourTime(entry.close);

  if (openTime && closeTime) {
    return `${openTime} - ${closeTime}`;
  }

  return openTime || closeTime;
};

const getShopDescription = (shopData) => {
  const description = String(shopData?.description || '').trim();
  const genericDescription = `${shopData?.name || ''}'s Shop`.trim().toLowerCase();

  if (!description || description.toLowerCase() === genericDescription.toLowerCase()) {
    return '';
  }

  return description;
};

const ShopAbout = ({ shopData, isOwner }) => {
  const { products } = useSelector((state) => state.products);
  const productCount = Array.isArray(products) ? products.length : 0;
  const totalReviewsLength = products && products.reduce((acc, product) => acc + (product.reviews?.length || 0), 0);
  const totalRatings = products && products.reduce(
    (acc, product) => acc + (product.reviews || []).reduce((sum, review) => sum + (review.rating || 0), 0),
    0
  );
  const averageRating = parseFloat((totalRatings / totalReviewsLength).toFixed(1)) || 0;
  const featuredReview = products
    ?.flatMap((product) => (product.reviews || []).map((review) => ({ ...review, productName: product.name })))
    ?.find((review) => review.comment || review.rating);
  const mapQuery = encodeURIComponent(shopData?.address || shopData?.name || 'MATRIX');
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${mapQuery}`;
  const reviewTarget = products?.[0]?._id ? `/product/${products[0]._id}` : `/shop/${shopData?.handle || ''}`;
  const description = getShopDescription(shopData);
  const openingHours = getShopOpeningHours(shopData?.openingHours);
  const hasAddress = hasListedValue(shopData?.address);
  const hasPhoneNumber = hasListedValue(shopData?.phoneNumber);
  const hasReviews = totalReviewsLength > 0;
  const detailItems = [
    ['Products', productCount],
    ['Rating', `${averageRating}/5`],
    ['Joined', formatJoinedDate(shopData?.createdAt)],
  ];

  return (
    <div className="shop-about-container">
      <div className="shop-about-header">
        <div className="shop-about-avatar-shell">
          <img src={shopData?.avatar?.url || '/placeholder-avatar.jpg'} alt="" className="shop-about-avatar" />
        </div>
        <div>
          <p className="shop-about-eyebrow">Shop Profile</p>
          <h2 className="shop-about-title">About {shopData?.name}</h2>
          {description && <p className="shop-about-subtitle">{description}</p>}
        </div>
      </div>

      <div className="shop-about-content">
        <section className="shop-about-location-card" aria-label="Shop location">
          <div className="shop-about-section-heading">
            <span>Location</span>
          </div>
          <div className="shop-about-location-copy">
            <h3>{shopData?.address || 'Location not listed'}</h3>
            {hasPhoneNumber && <p>{formatPhoneNumber(shopData?.phoneNumber)}</p>}
            {!hasAddress && !hasPhoneNumber && <p>Contact details have not been added yet.</p>}
          </div>
          {hasAddress && (
            <a href={directionsUrl} target="_blank" rel="noreferrer" className="shop-about-action-button">
              Get Directions
            </a>
          )}
        </section>

        {openingHours.length > 0 && (
          <section className="shop-about-hours-card" aria-label="Shop opening hours">
            <div className="shop-about-section-heading">
              <span>Opening Hours</span>
            </div>
            <div className="shop-about-hours-list">
              {openingHours.map((entry, index) => (
                <div className="shop-about-hours-row" key={`${entry.day || entry.label}-${index}`}>
                  {entry.day && <span className="shop-about-hours-day">{entry.day}</span>}
                  <p className="shop-about-hours-value">{formatOpeningHoursRange(entry)}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="shop-about-details">
          {detailItems.map(([label, value]) => (
            <div className="shop-about-detail-row" key={label}>
              <h5 className="shop-about-label">{label}</h5>
              <p className="shop-about-value">{value}</p>
            </div>
          ))}
        </div>

        <aside className={`shop-about-review-card ${!hasReviews ? 'shop-about-review-card-empty' : ''}`} aria-label="Shop review summary">
          <div>
            <p>Reviews</p>
            <h3>{averageRating}/5</h3>
            <span>{totalReviewsLength || 0} review{totalReviewsLength === 1 ? '' : 's'}</span>
          </div>
          {featuredReview ? (
            <blockquote>
              <p>{featuredReview.comment || `${featuredReview.rating}/5 rating for ${featuredReview.productName}`}</p>
              <cite>{featuredReview.user?.name || featuredReview.productName}</cite>
            </blockquote>
          ) : (
            <p className="shop-about-review-empty">No reviews yet.</p>
          )}
          {products?.[0]?._id && (
            <Link to={reviewTarget} className="shop-about-secondary-button">
              Leave a Review
            </Link>
          )}
        </aside>
      </div>

      {isOwner && (
        <div className="shop-about-actions">
          <Link to="/settings" className="shop-about-action-button">
            Edit Shop
          </Link>
        </div>
      )}
    </div>
  );
};

export default ShopAbout;
